<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { RBtn } from '@therobotcompany/robco-frontend-library';
import gettext from '@/i18n/gettext';
import type { ErpImportLog } from '@/models/Catalogue';
import { getErpImportLogs } from '@/api/CatalogueApi';
import formatDate from '@/helpers/DateFormatterHelper';
import { getErpSyncColor } from '@/helpers/ErpSyncHelper';

const { $gettext } = gettext;

const PAGE_SIZE = 20;

const props = defineProps<{
  modelValue: boolean;
  syncTimestamp: string | null;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'refreshSync'): void;
}>();

const logs = ref<ErpImportLog[]>([]);
const logsLoading = ref(false);
const logsOffset = ref(0);
const hasMoreLogs = ref(true);

const freshness = computed(() => {
  const color = getErpSyncColor(props.syncTimestamp);
  if (color === 'success') {
    return {
      color,
      icon: 'mdi-check-circle',
      text: $gettext('ERP data is up to date'),
    };
  }
  if (color === 'warning') {
    return {
      color,
      icon: 'mdi-alert-circle',
      text: $gettext('ERP data may be outdated'),
    };
  }
  return { color, icon: 'mdi-help-circle', text: $gettext('Never synced') };
});

const getModeText = (mode: string) => {
  switch (mode) {
    case 'update':
      return $gettext('Update');
    case 'create':
      return $gettext('Create');
    case 'upsert':
      return $gettext('Upsert');
    default:
      return mode;
  }
};

const loadLogs = async (reset: boolean = false) => {
  if (reset) {
    logsOffset.value = 0;
    logs.value = [];
    hasMoreLogs.value = true;
  }

  logsLoading.value = true;
  try {
    const result = await getErpImportLogs(PAGE_SIZE, logsOffset.value);
    logs.value = [...logs.value, ...result];
    hasMoreLogs.value = result.length === PAGE_SIZE;
    logsOffset.value += result.length;
  } catch (error) {
    console.error('Failed to load ERP import logs:', error);
  } finally {
    logsLoading.value = false;
  }
};

const handleClose = () => {
  emit('update:modelValue', false);
};

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      loadLogs(true);
    }
  },
);
</script>

<template>
  <v-dialog
    :model-value="props.modelValue"
    max-width="1000px"
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="d-flex flex-column" style="max-height: 80vh">
      <v-card-title class="d-flex align-center flex-shrink-0">
        <span>{{ $gettext('ERP Sync Status') }}</span>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" @click="handleClose" />
      </v-card-title>
      <v-divider />

      <!-- Freshness Widget (fixed) -->
      <div class="pa-4 flex-shrink-0">
        <v-alert
          :color="freshness.color"
          variant="tonal"
          :icon="freshness.icon"
        >
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="font-weight-medium">{{ freshness.text }}</div>
              <div v-if="syncTimestamp" class="text-caption mt-1">
                {{ $gettext('Last sync:') }}
                {{ formatDate(syncTimestamp, true) }}
              </div>
            </div>
            <r-btn
              variant="outlined"
              icon="mdi-refresh"
              @click="$emit('refreshSync')"
            >
              {{ $gettext('Refresh') }}
            </r-btn>
          </div>
        </v-alert>
      </div>

      <v-divider />

      <!-- Import History (scrollable) -->
      <v-card-text class="flex-grow-1 overflow-y-auto">
        <h3 class="mb-3">{{ $gettext('Import History') }}</h3>

        <v-alert
          v-if="!logsLoading && logs.length === 0"
          type="info"
          variant="tonal"
        >
          {{ $gettext('No imports have been performed yet.') }}
        </v-alert>

        <template v-else>
          <v-table density="compact">
            <thead>
              <tr>
                <th>{{ $gettext('Date') }}</th>
                <th>{{ $gettext('User') }}</th>
                <th>{{ $gettext('Filename') }}</th>
                <th>{{ $gettext('Mode') }}</th>
                <th class="text-right">{{ $gettext('Rows') }}</th>
                <th class="text-right">{{ $gettext('Created') }}</th>
                <th class="text-right">{{ $gettext('Updated') }}</th>
                <th class="text-right">{{ $gettext('Skipped') }}</th>
                <th class="text-right">{{ $gettext('Errors') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in logs" :key="log.uuid">
                <td class="text-no-wrap">
                  {{ formatDate(log.createdAt, true) }}
                </td>
                <td>{{ log.userName }}</td>
                <td>{{ log.filename || '-' }}</td>
                <td>
                  <v-chip size="small" variant="outlined">
                    {{ getModeText(log.mode) }}
                  </v-chip>
                </td>
                <td class="text-right">{{ log.totalRows }}</td>
                <td class="text-right">{{ log.itemsCreated }}</td>
                <td class="text-right">{{ log.itemsUpdated }}</td>
                <td class="text-right">{{ log.itemsSkipped }}</td>
                <td class="text-right">
                  <span :class="log.errorsCount > 0 ? 'text-error' : ''">
                    {{ log.errorsCount }}
                  </span>
                </td>
              </tr>
            </tbody>
          </v-table>

          <div
            v-if="hasMoreLogs && logs.length > 0"
            class="d-flex justify-center mt-4"
          >
            <r-btn
              variant="outlined"
              :loading="logsLoading"
              @click="loadLogs(false)"
            >
              {{ $gettext('Load more') }}
            </r-btn>
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
